import { Box, Button, Grid, Stack } from '@mui/material';
import { FormikProps } from 'formik/dist/types';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { SERVICES } from '@shared/constants/apiConfig';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { PageWrapper } from '@shared/styledComponents';

import { AddNewSubgroupModal } from './components/AddNewSubgroupModal';
import ConfigurationParameters from './components/ConfigurationParameters';
import { MenuWorkflow } from './components/MenuWorkflow';
import { initialQueueConfiguration } from './constants';
import { IAddSubGroup, IGroupsAndSubgroups, IQueueConfiguration, IRole } from './types';

export interface IQueueConfigurationProps extends Partial<FormikProps<IQueueConfiguration>> {
  setInitialState: Dispatch<SetStateAction<IQueueConfiguration>>;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  isEditMode: boolean;
  queueGroup: string;
  setQueueGroup: React.Dispatch<React.SetStateAction<string>>;
  queueSubGroup: string;
  setQueueSubGroup: React.Dispatch<React.SetStateAction<string>>
  queueConfigurationFetched?: IQueueConfiguration;
}

export const QueueConfigurationForm = ({
  setFieldValue,
  resetForm,
  values,
  errors,
  setInitialState,
  initialValues,
  setIsEditMode,
  isEditMode,
  queueGroup,
  setQueueGroup,
  queueSubGroup,
  setQueueSubGroup,
  queueConfigurationFetched,
  handleSubmit,
}: IQueueConfigurationProps) => {
  const [uniqueQueueGroupList, setUniqueQueueGroupList] = useState<string[]>([]);
  const [uniqueQueueSubGroupList, setUniqueQueueSubGroupList] = useState<string[]>([]);
  const [addNewSubgroupModalPosition, setAddNewSubgroupModalPosition] = useState(0);

  const { data: groupsAndSubGroups } = useSWR<IGroupsAndSubgroups[]>(
    `${SERVICES.CONFIGURATION}/queue-configurations/groups-and-subgroups`,
  );
  const { data: roles } = useSWR<IRole[]>(`${SERVICES.USER}/roles`);

  const onAddNewSubgroupSubmit = (data: IAddSubGroup) => {
    setInitialState({
      ...initialQueueConfiguration,
      group: queueGroup,
      subgroup: data.name,
      paramsType: data.paramsType,
    });
    setIsEditMode(false);
    setQueueSubGroup(data.name);
    setUniqueQueueSubGroupList((state) => state.concat([data.name]));
    setAddNewSubgroupModalPosition(0);
  };

  useEffect(() => {
    if (queueConfigurationFetched && isEditMode) {
      setInitialState(queueConfigurationFetched);
    }
  }, [queueConfigurationFetched]);

  useEffect(() => {
    const uniqueListOfGroups = Array.from(
      new Set(groupsAndSubGroups?.map((item: IGroupsAndSubgroups) => item?.group)),
    ).sort();
    if (uniqueListOfGroups) setUniqueQueueGroupList(uniqueListOfGroups);
  }, [groupsAndSubGroups]);

  const groupOptions = useMemo(() => (
    uniqueQueueGroupList.map((queue) => ({ value: queue, label: queue }))
  ), [uniqueQueueGroupList]);

  const subGroupOptions = useMemo(() => (
    uniqueQueueSubGroupList.map((queue) => ({ value: queue, label: queue }))
  ), [uniqueQueueSubGroupList]);

  useEffect(() => {
    setQueueSubGroup('');
    setIsEditMode(true);

    const listOfSubGroups = groupsAndSubGroups
      ?.filter((item: IGroupsAndSubgroups) => item?.group === queueGroup)
      .map((item) => item?.subgroup)
      .sort();

    if (listOfSubGroups) setUniqueQueueSubGroupList(listOfSubGroups);
  }, [queueGroup]);

  const handleQueueGroupChange = useCallback((value: string | null) => {
    setQueueGroup(value || '');
    setQueueSubGroup('');
  }, []);

  return (
    <PageWrapper>
      <Grid container spacing={6}>
        <Grid item md={6}>
          <Box mb={2} flexBasis="300px" maxWidth="300px">
            <SelectAutocomplete
              label="Queue group"
              options={groupOptions}
              value={queueGroup}
              onChange={handleQueueGroupChange}
            />
          </Box>

          {queueGroup && (
            <Stack direction="row" alignItems="center" gap={4}>
              <Box flexBasis="300px" maxWidth="300px">
                <SelectAutocomplete
                  label="Queue Sub group"
                  options={subGroupOptions}
                  value={queueSubGroup}
                  disabled={!isEditMode}
                  onChange={(value: string) => setQueueSubGroup(value || '')}
                />
              </Box>

              <Button variant="contained" color="primary" onClick={() => setAddNewSubgroupModalPosition(prevState => prevState + 1)}>
                Add new subgroup
              </Button>
            </Stack>
          )}

          {queueSubGroup && (
            <MenuWorkflow
              queueSubGroup={queueSubGroup}
              menuWorkflows={queueConfigurationFetched?.menuWorkflows || []}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
          )}
        </Grid>

        {queueSubGroup && (
          <ConfigurationParameters
            setFieldValue={setFieldValue}
            resetForm={resetForm}
            values={values}
            initialValues={initialValues}
            errors={errors}
            roles={roles}
          />
        )}
      </Grid>

      <AddNewSubgroupModal
        handleSubmit={onAddNewSubgroupSubmit}
        addNewSubgroupModalPosition={addNewSubgroupModalPosition}
        handleClose={() => setAddNewSubgroupModalPosition(0)}
      />
    </PageWrapper>
  );
};
